import {
  LAST_HANDLED_ORGANIZATION_SWITCH_EVENT_STORAGE_KEY,
  type OrganizationSwitchTabSyncEvent,
  isOrganizationSwitchEvent,
} from '@v2/commons/hooks/useTabSync'

export const PENDING_ORGANIZATION_SWITCH_STORAGE_KEY =
  'w360-organization-switch-pending-event'
export const LAST_EXPLICIT_LOGIN_AT_STORAGE_KEY =
  'w360-organization-switch-last-explicit-login-at'
export const ORGANIZATION_SWITCH_EVENT_MAX_AGE = 24 * 60 * 60 * 1000

export const getLastExplicitLoginAt = () => {
  try {
    const timestamp = Number(
      sessionStorage.getItem(LAST_EXPLICIT_LOGIN_AT_STORAGE_KEY),
    )
    return Number.isFinite(timestamp) ? timestamp : 0
  } catch {
    return 0
  }
}

export const getLastHandledOrganizationSwitchEventId = () => {
  try {
    return sessionStorage.getItem(
      LAST_HANDLED_ORGANIZATION_SWITCH_EVENT_STORAGE_KEY,
    )
  } catch {
    return null
  }
}

export const markOrganizationSwitchEventHandled = (eventId: string) => {
  try {
    sessionStorage.setItem(
      LAST_HANDLED_ORGANIZATION_SWITCH_EVENT_STORAGE_KEY,
      eventId,
    )
  } catch {
    // A storage failure must not prevent the acknowledgement reload.
  }
}

export const resetOrganizationSwitchSessionAfterLogin = () => {
  const loginAt = Date.now()
  try {
    sessionStorage.setItem(LAST_EXPLICIT_LOGIN_AT_STORAGE_KEY, String(loginAt))
  } catch {
    // Redux still records this boundary when persistence is unavailable.
  }
  clearPendingOrganizationSwitchEvent()
  return loginAt
}

export const getPendingOrganizationSwitchEvent =
  (): OrganizationSwitchTabSyncEvent | null => {
    try {
      const serializedEvent = sessionStorage.getItem(
        PENDING_ORGANIZATION_SWITCH_STORAGE_KEY,
      )
      if (!serializedEvent) return null

      const event = JSON.parse(serializedEvent)

      if (
        !isOrganizationSwitchEvent(event) ||
        Date.now() - event.timestamp > ORGANIZATION_SWITCH_EVENT_MAX_AGE ||
        event.timestamp <= getLastExplicitLoginAt() ||
        event.eventId === getLastHandledOrganizationSwitchEventId()
      ) {
        clearPendingOrganizationSwitchEvent()
        return null
      }

      return event
    } catch {
      clearPendingOrganizationSwitchEvent()
      return null
    }
  }

export const setPendingOrganizationSwitchEvent = (
  event: OrganizationSwitchTabSyncEvent,
) => {
  try {
    sessionStorage.setItem(
      PENDING_ORGANIZATION_SWITCH_STORAGE_KEY,
      JSON.stringify(event),
    )
  } catch {
    // The live blocking state remains available in Redux.
  }
}

export const clearPendingOrganizationSwitchEvent = () => {
  try {
    sessionStorage.removeItem(PENDING_ORGANIZATION_SWITCH_STORAGE_KEY)
  } catch {
    // Storage cleanup must not interrupt login or acknowledgement.
  }
}
